import { apiClient } from '@/shared/api/apiClient'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

export type IngestionDiagnosticsListItem = {
  origin: 'product' | 'packaging'
  id: string
  name: string
  type: string
  createdAt: string
  ingestionStatus: string | null
  fileUploadStatus?: string | null
  filePathInBucket?: string
  ingestionStatusUpdatedAt: string | null
  ingestedStartedAt: string | null
  /** Latest RAG pipeline stage mirrored onto Lex via webhook (not live OCR job status). */
  ingestionStage: string | null
  ingestionErrorCode: string | null
  ragJobId: string | null
  ingestionEmbeddingTokens: number | null
  pagesChunksMismatch?: boolean
  product?: { id: string; name: string | null }
  packagingItem?: {
    id: string
    name: string
    packagingSupplierId: string
    packagingSupplierName: string
  }
  organization: { id: string; name: string }
}

export type IngestionEventSeverity = 'success' | 'neutral' | 'failure' | 'warning'

export type IngestionTimelinePhase = {
  id: 'lex' | 'rag' | 'terminal'
  label: string
  tone: 'default' | 'failure'
  summary?: string | null
  items: Array<{
    at: string
    title: string
    description?: string | null
    severity: IngestionEventSeverity
    code?: string | null
  }>
  events?: Array<{
    at: string
    eventType: string
    severity: IngestionEventSeverity
    actor?: string | null
    details: Record<string, unknown>
  }>
}

export type IngestionDetailResponse = {
  source: {
    origin: 'product' | 'packaging'
    id: string
    name: string
    type: string
    createdAt: string
    ingestionStatus: string | null
    fileUploadStatus?: string | null
    filePathInBucket: string
    ingestionStatusUpdatedAt: string | null
    ingestedStartedAt: string | null
    ingestionStage: string | null
    ingestionProgress: unknown
    ingestionError: string | null
    ingestionErrorCode: string | null
    ragJobId: string | null
    ingestionEmbeddingTokens?: number | null
    ingestionEmbeddingModel?: string | null
    ocrExpectedPages?: number | null
    ocrCompletedPages?: number | null
    ocrFailedPages?: number | null
    chunkCount?: number | null
    distinctChunkPages?: number | null
    pagesChunksMismatch?: boolean
    product?: { id: string; name: string | null }
    packagingItem?: {
      id: string
      name: string
      packagingSupplierId: string
      packagingSupplierName: string
    }
    organization: { id: string; name: string }
  }
  lexSignedSourceUrl: string | null
  rag: {
    available: boolean
    error?: string
    ingestion?: Record<string, unknown>
    ocrJob?: {
      pages: Array<{
        page_number: number
        status: string
        page_type?: string | null
        n_vlm_calls?: number | null
        cost_estimate_usd?: number | null
        duration_ms?: number | null
        attempt_count: number
        error_message?: string | null
      }>
      partial_result?: boolean
      has_unrecovered_truncation?: boolean
      truncation_severe?: boolean
      [key: string]: unknown
    }
    artifacts?: {
      source_url?: string | null
      result_url?: string | null
      expires_in_seconds: number
    }
    statusComparison?: {
      lex: string
      ingestion: string | null
      ocr: string | null
      mismatched: boolean
    }
    coverageComparison?: {
      expectedPages: number | null
      completedPages: number | null
      failedPages: number | null
      requiredPages: number | null
      chunkCount: number | null
      distinctChunkPages: number | null
      pagesChunksMismatch: boolean
      source: 'lex' | 'rag'
    }
    sentryTraceUrl?: string | null
  }
  phases: IngestionTimelinePhase[]
}

export type IngestionErrorSummary = {
  totalFailures: number
  byErrorCode: Array<{ errorCode: string; count: number }>
  byOrganization: Array<{
    organizationId: string
    organizationName: string
    failures: number
    failureRate: number
  }>
  trend: Array<{ day: string; failures: number; failureRate: number }>
  needsAttention: {
    stuck: number
    missingRagJobId: number
    pagesChunksMismatch: number
  }
}

export const INGESTION_DIAGNOSTICS_QUERY_KEY = 'ingestionDiagnostics'

export function useIngestionDiagnosticsList(
  filters: {
    organizationId?: string
    status?: string
    errorCode?: string
    attention?: 'stuck' | 'missingRagJobId' | 'pagesChunksMismatch'
    origin?: 'product' | 'packaging' | 'all'
    searchQuery?: string
  },
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: [INGESTION_DIAGNOSTICS_QUERY_KEY, 'list', filters],
    enabled: options?.enabled ?? true,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get<{
        items: IngestionDiagnosticsListItem[]
        nextCursor: string | null
      }>('/admin/diagnostics/ingestions', {
        params: {
          organizationId: filters.organizationId,
          status: filters.status,
          errorCode: filters.errorCode,
          attention: filters.attention,
          origin: filters.origin ?? 'all',
          q: filters.searchQuery,
          cursor: pageParam,
        },
      })
      return response.data
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
}

export function useIngestionDetail(sourceId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [INGESTION_DIAGNOSTICS_QUERY_KEY, 'detail', sourceId],
    enabled: Boolean(sourceId) && (options?.enabled ?? true),
    queryFn: async () => {
      const response = await apiClient.get<IngestionDetailResponse>(
        `/admin/diagnostics/ingestions/${sourceId}`
      )
      return response.data
    },
  })
}

export type IngestionOcrResultResponse = {
  pages?: Array<{
    page_number?: number
    page?: number
    content?: unknown
    text?: string
  }>
}

export function useIngestionOcrResult(
  sourceId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [INGESTION_DIAGNOSTICS_QUERY_KEY, 'ocrResult', sourceId],
    enabled: Boolean(sourceId) && (options?.enabled ?? true),
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false
      }
      return failureCount < 3
    },
    queryFn: async () => {
      const response = await apiClient.get<IngestionOcrResultResponse>(
        `/admin/diagnostics/ingestions/${sourceId}/ocr-result`
      )
      return response.data
    },
  })
}

export function useIngestionErrorSummary(
  windowDays: 7 | 30 | 90,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [INGESTION_DIAGNOSTICS_QUERY_KEY, 'errorSummary', windowDays],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<IngestionErrorSummary>(
        '/admin/diagnostics/ingestion-errors/summary',
        { params: { windowDays } }
      )
      return response.data
    },
  })
}

export function useFailedIngestionsList(
  filters: {
    windowDays: 7 | 30 | 90
    organizationId?: string
    errorCode?: string
  },
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: [INGESTION_DIAGNOSTICS_QUERY_KEY, 'failed', filters],
    enabled: options?.enabled ?? true,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get<{
        items: Array<
          IngestionDiagnosticsListItem & {
            failedAt: string | null
            ingestionError: string | null
          }
        >
        nextCursor: string | null
      }>('/admin/diagnostics/ingestion-errors', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      })
      return response.data
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
}
